import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import InventoryIcon from "src/assets/svg-icons/side-bar/inventory.svg";
import * as yup from "yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CalcItem from "../../all/proudction-recipe/calculation-item";
import { useGetBranches } from "src/api/generic";
import { useGetProEntryById } from "src/api/inventory/production";

const schema = yup.object().shape({
  branch_id: yup.number().required("required"),
  recipe_quantity: yup.number().required("required"),
  items: yup
    .array()
    .of(
      yup.object().shape({
        item_id: yup.number().required("required"),
        item_name: yup.string(),
        item_sku: yup.string(),
        item_unit: yup.string(),
        item_unit_cost: yup.number(),
        item_quantity: yup.number().typeError("Required").required("required"),
        // calculations
        total: yup.number(),
      })
    )
    .required("Required"),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const ProductionEntryDetails = () => {
  const navigate = useNavigate();
  const { t: generalT } = useTranslation("general");
  const { t } = useTranslation("inventory");
  const { entryId } = useParams();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const { fields, append, remove, update } = useFieldArray({
    name: "items",
    control,
  });

  const {
    data: entryData,
    status: getEntryStatus,
    refetch: refetchEntry,
    isFetching: isFetchingEntry,
    isLoading: isLoadingEntry,
  } = useGetProEntryById({
    enabled: !!entryId,
    id: entryId as string,
  });

  const { data: branchesData } = useGetBranches();

  const navigateToInventory = () => {
    navigate("/inventory/production");
  };

  useEffect(() => {
    if (getEntryStatus === "success") {
      reset({
        branch_id: entryData.data?.branch?.id,
        recipe_quantity: entryData.data?.quantity,
        items: entryData.data.product_pro_items.map((item: any) => {
          return {
            item_id: item.item?.id,
            item_name: item.item?.name,
            item_sku: item.item?.sku,
            item_unit: item.item?.unit?.name,
            item_quantity: item.quantity,
            item_unit_cost: item?.unit_cost,
          };
        }),
      });
    }
  }, [getEntryStatus]);

  let totalItems = 0;

  (watch("items") || []).forEach((item) => {
    totalItems += item.total ?? 0;
  });

  return (
    <Stack gap={3} p={{ xs: 2, sm: 4 }}>
      <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={InventoryIcon} width={"32px"} alt="" />
        </Box>
        <Stack>
          <Typography fontSize={"30px"} lineHeight={"38.4px"}>
            {entryData?.data?.item?.name}
          </Typography>
          <Typography lineHeight={"38.4px"}>
            {t("totalCost")}: <strong>{entryData?.data?.total_cost?.toFixed(3)} SAR</strong>
          </Typography>
        </Stack>
      </Stack>
      {/* divider */}
      <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} />

      <Stack spacing={2} direction={"row"} mb={3} alignItems={"center"}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInventory}
          sx={{ cursor: "pointer" }}
        >
          {t("inventory")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInventory}
          sx={{ cursor: "pointer" }}
        >
          {t("production")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {/* {mode === "add" ? generalT("add") : entryData?.data.reference} */}
            {entryData?.data?.reference}
          </Typography>
        </Box>
      </Stack>

      {isLoadingEntry ? (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          minHeight={"250px"}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack gap={2}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={{ xs: 2, md: 10 }}
              border={"1px solid #F0F0F0"}
              borderRadius={"12px"}
              p={2}
            >
              <Typography color={"#667085"}>{t("totalProIs")}</Typography>
              <Controller
                name={`recipe_quantity`}
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    label={`${t("recipeQuantity")}`}
                    variant="outlined"
                    {...field}
                    error={!!errors?.recipe_quantity}
                    helperText={errors?.recipe_quantity?.message}
                    fullWidth
                    size="small"
                    sx={{ minWidth: 170, width: 170 }}
                    disabled
                  />
                )}
              />
              <Stack direction={"row"} gap={0.5}>
                <Typography color={"#667085"}>{t("unit")}:</Typography>
                <Typography color={"#667085"} fontWeight={600}>
                  {entryData.data?.item?.unit?.name}
                </Typography>
              </Stack>
            </Stack>
            <Stack gap={2}>
              <Typography
                color={"#344054"}
                fontSize={"14px"}
                fontWeight={700}
                lineHeight={"16.8px"}
              >
                {generalT("branch")}
              </Typography>
              <FormControl fullWidth size="small" error={!!errors.branch_id}>
                <InputLabel id="demo-simple-select-label">
                  {generalT("branch")}
                </InputLabel>
                <Controller
                  name="branch_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      key={field.value}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={generalT("branch")}
                      sx={{ maxWidth: "220px" }}
                      disabled
                    >
                      {branchesData?.data?.map(
                        ({ id, name, active_subscriptions }) => {
                          return (
                            <MenuItem
                              key={id}
                              value={`${id}`}
                              // disabled={
                              //   !active_subscriptions?.package.has_items
                              // }
                            >
                              {name}
                            </MenuItem>
                          );
                        }
                      )}
                    </Select>
                  )}
                />
                <FormHelperText id="my-helper-text">
                  {generalT(`${errors.branch_id?.message ?? ""}`)}
                </FormHelperText>
              </FormControl>
            </Stack>

            {fields.map((itemField, index) => {
              const totalCost =
                watch("items")[index].item_quantity *
                (watch("items")[index].item_unit_cost ?? 0);
              return (
                <Box
                  key={itemField.id}
                  // padding={"14px 16px"}
                  borderRadius={1.5}
                  // border={"2px solid #F0F0F0"}
                  // boxShadow={"rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"}
                >
                  <Grid
                    container
                    spacing={{ xs: 1, md: 2 }}
                    alignItems={"center"}
                  >
                    <Grid item xs={11} sm={11}>
                      <Stack
                        direction={"row"}
                        gap={{ xs: 2, md: 6.5 }}
                        alignItems={"center"}
                        flexWrap={"wrap"}
                      >
                        <Box sx={{ minWidth: 180, width: 180 }}>
                          <Typography
                            color={"#667085"}
                            fontSize={"13px"}
                            lineHeight={"24px"}
                          >
                            {t("item")}
                          </Typography>
                          <Typography
                            color={"#667085"}
                            fontSize={"13px"}
                            lineHeight={"24px"}
                            fontWeight={700}
                          >
                            {itemField.item_name} ({itemField.item_unit})
                          </Typography>
                        </Box>

                        <Box sx={{ minWidth: 80, width: 80 }}>
                          <Typography
                            color={"#667085"}
                            fontSize={"13px"}
                            lineHeight={"24px"}
                          >
                            {t("sku")}
                          </Typography>
                          <Typography
                            color={"#667085"}
                            fontSize={"13px"}
                            lineHeight={"24px"}
                            fontWeight={700}
                            whiteSpace={"nowrap"}
                          >
                            {itemField.item_sku}
                          </Typography>
                        </Box>

                        <Controller
                          name={`items.${index}.item_quantity`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              type="number"
                              label={`${t("qnty")}`}
                              variant="outlined"
                              {...field}
                              error={!!errors.items?.[index]?.item_quantity}
                              helperText={
                                errors.items?.[index]?.item_quantity?.message
                              }
                              fullWidth
                              size="small"
                              sx={{ minWidth: 170, width: 170 }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Typography fontSize={"14px"}>
                                      {itemField.item_unit}
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                              disabled
                            />
                          )}
                        />
                        {/* calculations */}
                        <CalcItem
                          label={t("unitCost")}
                          value={`${itemField.item_unit_cost?.toFixed(3) ?? 0}`}
                        />
                        <CalcItem
                          label={t("total")}
                          value={`${totalCost.toFixed(3)}`}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={1} md={1}></Grid>
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default ProductionEntryDetails;
